import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, Stethoscope, User, ArrowRight } from 'lucide-react';

const RoleSelection = () => {
  const navigate = useNavigate();

  // Clear any existing session on mount
  useEffect(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    sessionStorage.clear();
  }, []);

  const roles = [
    {
      id: 'admin',
      title: 'Admin Portal',
      description: 'Access Flywheel HQ dashboard, manage therapists, monitor KPIs',
      icon: Shield,
      email: 'admin@physiomobile.com',
      password: 'admin123',
      color: 'from-purple-500 to-purple-600',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
    },
    {
      id: 'therapist',
      title: 'Therapist Portal',
      description: 'Track performance, manage referrals, submit content',
      icon: Stethoscope,
      email: 'therapist1@physiomobile.com',
      password: 'therapist123',
      color: 'from-blue-500 to-blue-600',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
    {
      id: 'customer',
      title: 'Patient Portal',
      description: 'View rehab journey, earn points, redeem rewards',
      icon: User,
      email: 'customer1@example.com',
      password: 'customer123',
      color: 'from-green-500 to-green-600',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
    },
  ];

  const handleLogin = (role) => {
    // Store the selected role credentials temporarily
    sessionStorage.setItem('demo_email', role.email);
    sessionStorage.setItem('demo_password', role.password);
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <img
            src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
            alt="Physiomobile Logo"
            className="h-20 mx-auto mb-6"
          />
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-800 mb-3">
            Physiomobile Flywheel Engine
          </h1>
          <p className="text-lg text-muted-foreground">
            Choose your portal to continue
          </p>
        </div>

        {/* Role Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {roles.map((role) => (
            <Card
              key={role.id}
              className="p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
              onClick={() => handleLogin(role)}
              data-testid={`role-card-${role.id}`}
            >
              <div className={`w-16 h-16 rounded-2xl ${role.iconBg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                <role.icon className={`w-8 h-8 ${role.iconColor}`} />
              </div>

              <h2 className="font-heading text-2xl font-bold text-gray-800 mb-3">
                {role.title}
              </h2>
              
              <p className="text-muted-foreground mb-6 min-h-[3rem]">
                {role.description}
              </p>

              <div className="mb-6 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Demo Credentials:</p>
                <p className="font-mono text-xs text-gray-700">{role.email}</p>
                <p className="font-mono text-xs text-gray-700">{role.password}</p>
              </div>

              <Button
                className="w-full group-hover:shadow-md"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLogin(role);
                }}
                data-testid={`login-${role.id}-button`}
              >
                Login as {role.title.split(' ')[0]}
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Card>
          ))}
        </div>

        {/* Info Section */}
        <Card className="p-6 bg-blue-50 border-blue-200">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl">ℹ️</span>
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">About This Demo</h3>
              <p className="text-sm text-blue-800">
                This is a production-ready Physiomobile Flywheel Engine with complete multi-role system.
                Each portal has unique features:
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                <li><strong>Admin</strong>: Monitor KPIs, manage therapists, approve content, track corporate leads</li>
                <li><strong>Therapist</strong>: View performance, earn tier upgrades, create referrals, submit content</li>
                <li><strong>Patient</strong>: Track sessions, earn/redeem points, manage family, refer friends</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RoleSelection;
